import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Grid, Paper, Stack } from "@mui/material";
import LocalizedInput from "../LocalizedInput";
import TextInput from "../TextInput";
import { emptyLocalizedFields } from "../../../config/localization";
import isEqual from "lodash.isequal";
import { useNavigate } from "react-router-dom";
import { StickyFormActions } from "../StickyFormActions";
import { CardHeading, cardSx } from "../CardHeading";
import { useUnsavedChangesGuard } from "../../../contexts/hooks/useUnsavedChangesGuard";

export const FeatureGroupForm = ({ props, onSave }) => {
  const navigate = useNavigate();
  const saveInProgress = useRef(false);

  const defaultFeatureGroup = useMemo(
    () => ({
      featureGroupCode: props.row.featureGroupCode ?? "",
      featureGroupName: props.row.featureGroupName || emptyLocalizedFields,
      featureGroupDisplayName:
        props.row.featureGroupDisplayName || emptyLocalizedFields,
    }),
    [props.row],
  );

  const [currentFeatureGroup, setCurrentFeatureGroup] = useState({
    ...defaultFeatureGroup,
  });
  const [currentFeatureGroupOriginal, setCurrentFeatureGroupOriginal] =
    useState({ ...defaultFeatureGroup });

  useEffect(() => {
    if (!defaultFeatureGroup.featureGroupCode) return;
    setCurrentFeatureGroup(defaultFeatureGroup);
    setCurrentFeatureGroupOriginal(defaultFeatureGroup);
  }, [defaultFeatureGroup]);

  const saveText = props?.saveText ?? "Save";
  const isBusy = Boolean(props.loading || props.uploading);
  const hasChanges = !isEqual(currentFeatureGroup, currentFeatureGroupOriginal);
  const { confirmLeave } = useUnsavedChangesGuard(hasChanges);

  const handleSave = async () => {
    if (isBusy || saveInProgress.current || !hasChanges) return;
    saveInProgress.current = true;

    try {
      const saved = await onSave(currentFeatureGroup);
      if (saved !== true) return;

      if (!props.isUpdate) {
        setCurrentFeatureGroup({ ...defaultFeatureGroup });
        setCurrentFeatureGroupOriginal({ ...defaultFeatureGroup });
      } else {
        setCurrentFeatureGroupOriginal(currentFeatureGroup);
      }
    } finally {
      saveInProgress.current = false;
    }
  };

  const handleCancel = async () => {
    if (isBusy || !(await confirmLeave())) return;
    navigate("/featuregroups");
  };

  return (
    <Box
      sx={{
        "& input, & textarea, & button": {
          scrollMarginBottom: { xs: 168, sm: 96 },
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="feature-group-details-heading"
            aria-busy={isBusy}
            sx={cardSx}
          >
            <CardHeading
              id="feature-group-details-heading"
              title="Feature group details"
              helper="Identify the feature group and provide its localized names."
            />
            <Stack spacing={3}>
              <TextInput
                label="Feature Group Code"
                required
                value={currentFeatureGroup.featureGroupCode}
                disabled={props.isUpdate || isBusy}
                helperText={
                  props.isUpdate
                    ? "The feature group code can't be changed after creation"
                    : undefined
                }
                onChange={(event) => {
                  setCurrentFeatureGroup((prev) => ({
                    ...prev,
                    featureGroupCode: event.target.value,
                  }));
                }}
              />
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <LocalizedInput
                    variant="modern"
                    label="Feature Group Name"
                    value={currentFeatureGroup.featureGroupName}
                    onChange={(value) => {
                      setCurrentFeatureGroup((prev) => ({
                        ...prev,
                        featureGroupName: value,
                      }));
                    }}
                    multiline
                    rows={2}
                    disabled={isBusy}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <LocalizedInput
                    variant="modern"
                    label="Feature Group Display Name"
                    value={currentFeatureGroup.featureGroupDisplayName}
                    onChange={(value) => {
                      setCurrentFeatureGroup((prev) => ({
                        ...prev,
                        featureGroupDisplayName: value,
                      }));
                    }}
                    multiline
                    rows={2}
                    disabled={isBusy}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      <StickyFormActions
        hasChanges={hasChanges}
        saving={isBusy}
        saveText={saveText}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </Box>
  );
};
